import { FormField, FormResponse, ConditionalValidation } from '../types/form';

export const isFieldConditionallyRequired = (
  field: FormField,
  responses: FormResponse
): boolean => {
  if (!field.validation?.conditionalRequired) {
    return field.validation?.required ?? false;
  }

  const { dependsOn, condition } = field.validation.conditionalRequired;
  const parentValue = responses[dependsOn];

  if (parentValue === undefined || parentValue === null || parentValue === '') {
    return false;
  }

  if (condition.value !== undefined) {
    if (Array.isArray(condition.value)) {
      return condition.value.includes(parentValue as string);
    }
    return parentValue === condition.value;
  }

  if (condition.notValue !== undefined) {
    if (Array.isArray(condition.notValue)) {
      return !condition.notValue.includes(parentValue as string);
    }
    return parentValue !== condition.notValue;
  }

  if (condition.includes !== undefined && Array.isArray(parentValue)) {
    return (parentValue as string[]).includes(condition.includes);
  }

  if (condition.notIncludes !== undefined && Array.isArray(parentValue)) {
    return !(parentValue as string[]).includes(condition.notIncludes);
  }

  return false;
};

export const isFieldVisible = (
  field: FormField,
  responses: FormResponse
): boolean => {
  if (!field.validation?.conditionalRequired) {
    return true;
  }

  return isFieldConditionallyRequired(field, responses);
};

export const shouldSkipField = (
  field: FormField,
  responses: FormResponse
): boolean => {
  if (!field.validation?.conditionalRequired) {
    return false;
  }

  return !isFieldConditionallyRequired(field, responses);
};

export const getVisibleFields = (
  fields: FormField[],
  responses: FormResponse
): FormField[] => {
  return fields.filter(field => isFieldVisible(field, responses));
};

export const getRequiredFields = (
  fields: FormField[],
  responses: FormResponse
): FormField[] => {
  return fields.filter(field => {
    if (field.validation?.conditionalRequired) {
      return isFieldConditionallyRequired(field, responses);
    }
    return field.validation?.required ?? false;
  });
};

export const validateField = (
  field: FormField,
  value: any,
  responses: FormResponse
): { isValid: boolean; error?: string } => {
  const isRequired = field.validation?.conditionalRequired
    ? isFieldConditionallyRequired(field, responses)
    : field.validation?.required ?? false;

  if (isRequired && (value === undefined || value === null || value === '')) {
    return { isValid: false, error: 'This field is required' };
  }

  if (!value) {
    return { isValid: true };
  }

  if (field.validation?.pattern) {
    const regex = new RegExp(field.validation.pattern);
    if (!regex.test(value as string)) {
      return {
        isValid: false,
        error: field.type === 'text' && field.question.toLowerCase().includes('email')
          ? 'Please enter a valid email address'
          : 'Please enter a valid format'
      };
    }
  }

  if (field.validation?.min !== undefined && (value as number) < field.validation.min) {
    return { isValid: false, error: `Value must be at least ${field.validation.min}` };
  }

  if (field.validation?.max !== undefined && (value as number) > field.validation.max) {
    return { isValid: false, error: `Value must be at most ${field.validation.max}` };
  }

  return { isValid: true };
};
